import { Placeholder } from 'rsuite';
import {
	Avatar,
	AvatarSize,
	Button,
	ButtonAppearance,
	ButtonColor,
	ButtonSize,
	CloseIcon,
	Dialog,
	Input, MiddleEllipsisWhisper,
	RightArrow,
	Zoom,
	DirectionIcon,
} from '@aleph/shared-components';
import { useQuery } from '@apollo/client';
import { AuthGraphAdvertiserListDocument } from 'wb-spotify/graphql/authgraph/generated';
import React, {
	ChangeEvent, FC, useEffect, useState,
} from 'react';
import { debounce } from 'lodash';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import spotifyLogo from 'wb-spotify/assets/spotify-logo.png';

type FullScreenAgencySelectorProps = {
	onAgencySelected: (agencyId: string) => void;
	onClose: () => void;
	isOpen: boolean;
};

const FullScreenAgencySelector: FC<FullScreenAgencySelectorProps> = ({
	onAgencySelected,
	onClose,
	isOpen,
}) => {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState('');
	const [search, setSearch] = useState('');

	useEffect(() => {
		debounce(setSearch, 300)(inputValue);
	}, [inputValue]);

	const { data, loading } = useQuery(AuthGraphAdvertiserListDocument, {
		variables: {
			search: `*${search}*`,
		},
	});

	useEffect(() => {
		const closeOnEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', closeOnEsc);

		return () => {
			window.removeEventListener('keydown', closeOnEsc);
		};
	}, []);

	return (
		<Dialog
			onClose={onClose}
			isOpen={isOpen}
		>
			<Dialog.Header
				preset={{
					prefix: <DirectionIcon />,
					title:  t('sc: advertisers.whose_connecting', 'Whose account are you connecting?'),
					suffix: <Button
						leftNode={
							<CloseIcon />
						}
						appearance={ButtonAppearance.GHOST}
						color={ButtonColor.GRAY}
						size={ButtonSize.LARGE}
						onClick={onClose}
					/>,
				}}
			/>
			<Dialog.Content>
				<Input
					placeholder={t('sc: advertisers.search', 'Search advertisers...')}
					onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
					value={inputValue}
					prefix={
						<Zoom
							height="24"
						/>
					}
					size="large"
					className="w-full"
					clearable
					autoFocus
				/>
				{<div className="h-[25vh] overflow-y-auto mt-[8px] max-w-lg">
					{!loading
							&& data?.advertiserAccounts?.items?.map(agencyAccount => (
								<div
									className={classNames(
										'font-medium text-[16px] h-[48px] flex justify-between items-center',
										'px-[8px] text-black border-b border-b-gray-200 last:border-none',
										'cursor-pointer hover:text-blue group',
									)}
									onClick={() => onAgencySelected(agencyAccount.id)}
									key={agencyAccount?.id}
								>
									<div className="flex flex-1 gap-[6px] whitespace-nowrap items-center h-full overflow-hidden">
										<Avatar
											// avatarSource={agencyAccount?.agencyAccountProfilePictureUrl}
											avatarSource={spotifyLogo}
											size={AvatarSize.Small}
										/>
										<MiddleEllipsisWhisper value={<div>{agencyAccount?.agencyAccountName}</div>} />
									</div>
									<div className="flex items-center gap-[8px]">
										<motion.div
											className="text-[12px] font-normal opacity-0 group-hover:opacity-100"
											animate={{ x: 3 }}
											transition={{ repeat: Infinity, repeatType: 'reverse', duration: 0.3 }}
										>
											{t('sc: advertisers.connect', 'Connect')}
										</motion.div>
										<RightArrow
											width="16"
											height="16"
										/>
									</div>
								</div>
							))
					}

					{loading
							&& new Array(10).fill(undefined).map((_, index) => (
								<div
									key={index}
									className="h-[48px] flex items-center border-b
											border-b-gray-200 last:border-none"
								>
									<Placeholder.Graph
										height={32}
										active
									/>
								</div>
							))
					}

					{!loading && data?.advertiserAccounts?.items?.length === 0
							&& <div
								className="text-gray-600 font-[18px] h-full w-full items-center justify-center flex"
							>
								{t('sc: advertisers.no_data', 'No agencies found')}
							</div>
					}
				</div>
				}
			</Dialog.Content>
		</Dialog>
	);
};

export default FullScreenAgencySelector;
